<template>
  <div
    class="pattern px-3 pt-1 pb-2 mb-2"
    ref="patternWrap"
    v-hotkey.prevent="keymap"
  >
    <b-modal
      v-if="showImportWord"
      v-model="showImportWord"
      hide-backdrop
      :static="true"
      :lazy="true"
      id="import-word"
      size="sm"
      header-class="bg-background py-1"
      footer-class="pt-0"
      :title="hebrew ? 'ייבוא מילה' : 'Import word'"
      @show="onImportShow"
      @hidden="
        importFocusOut, (wordSelectedFromDD = false), (notInList = false)
      "
      :ok-disabled="disableImportOk"
      @ok="wordImported"
      :ok-title="hebrew ? 'השתמש' : 'Import'"
      :cancel-title="hebrew ? 'סגור' : 'Cancel'"
    >
      <small>{{ hebrew ? "הזן מילת חיפוש" : "Enter search word" }}</small>
      <MainSearch
        ref="mainSearch"
        @word-selected=";(wordSelectedFromDD = true), (notInList = false)"
        @word-not-found="notInList = true"
        @word-typed="(w) => (searchWordInput = w)"
      />
      <b-form-checkbox
        v-show="!disableImportOk"
        class=""
        id="checkbox-import"
        v-model="importNikudOnly"
        name="checkbox-import"
      >
        <small>{{
          hebrew ? "התייחס רק לניקוד" : "Ignore typed letters"
        }}</small>
      </b-form-checkbox>
    </b-modal>
    <div class="word-wrap">
      <div class="d-flex justify-content-between my-2">
        <div class="my-1">{{ hebrew ? "תבנית חיפוש" : "Search Pattern" }}</div>
        <b-btn
          class="bg-transparent text-primary import-btn mb-1 shadow-none border-0 p-0 text-underline"
          @click="showImportWord = true"
        >
          {{ hebrew ? "ייבוא מילה" : "Import word" }}
        </b-btn>
      </div>
      <label>{{ hebrew ? "אות" : "Letter" }}</label>
      <span
        v-for="(n, i) in 8"
        :key="i"
        :class="{
          root: tokens[i].rootLetter === dageshRootOptions.WITH,
          'no-root': tokens[i].rootLetter === dageshRootOptions.NONE,
        }"
        class="input-wrap d-inline-block position-relative"
      >
        <input
          class="bg-white text-center border-0 p-0 text-center mid-text"
          :class="{
            'index-selected': i === currentIndex && wordFocused,
            'word-index-selected': i === currentIndex && !wordFocused,
            'text-white': tokens[i].letters.length === 0,
            'f-narkis': tokens[i].letters.length === 1,
            'optional-letter':
              tokens[i].optional === optionalLetterOptions.OPTIONAL,
          }"
          maxlength="1"
          ref="wordPattern"
          @focus="
            setCurrentLetter(i)
            wordFocused = true
            currentIndex = i
          "
          @keydown="onLetterInput"
          @click="
            currentIndex = i
            setCurrentLetter(i)
            wordFocused = true
          "
          @keydown.right="rightArrowPress(i)"
          @keydown.left="leftArrowPress(i)"
          @keydown.up="upDownArrowPress(i)"
          @keydown.down="upDownArrowPress(i)"
          :value="
            tokens[i].letters.includes('*')
              ? '*'
              : tokens[i].letters.length > 1
              ? tokens[i].letters.length
              : tokens[i].letters.length === 1
              ? tokens[i].letters[0]
              : ''
          "
        />
      </span>
    </div>

    <div class="nikud-wrap">
      <label>{{ hebrew ? "ניקוד" : "Nikud" }}</label>
      <span
        v-for="(n, i) in 8"
        :key="i"
        class="input-wrap d-inline-block position-relative"
        :class="{
          'dagesh-letter': tokens[i].dagesh === dageshRootOptions.WITH,
          'not-dagesh-letter': tokens[i].dagesh === dageshRootOptions.NONE,
          'bg-white':
            !tokens[i].letters ||
            (tokens[i].letters && !tokens[i].letters.includes('*')),
          'bg-background':
            !tokens[i].letters ||
            (tokens[i].letters && tokens[i].letters.includes('*')),
        }"
      >
        <input
          ref="nikudPattern"
          class="d-block text-center border-0 text-center position-relative bg-transparent"
          :class="{
            'index-selected': i === currentIndex && !wordFocused,
            'word-index-selected': i === currentIndex && wordFocused,
            'position-absolute p-0': isSafari,
            'f-narkis':
              patternLength(i) !== nikudVals.length &&
              !isSafari &&
              tokens[i].pattern[0] !== nikudVals[0],
            nikud:
              patternLength(i) == 1 &&
              tokens[i].pattern[0] &&
              tokens[i].pattern[0].split('').indexOf('ש') === -1 &&
              Object.keys(syllables).indexOf(tokens[i].pattern[0]) == -1 &&
              tokens[i].pattern[0] !== nikudVals[0] &&
              tokens[i].pattern[0] !== '?',
            'mid-text':
              tokens[i].pattern[0] &&
              tokens[i].pattern[0].split('').indexOf('ש') === -1,
            hirik: patternLength(i) === 1 && tokens[i].pattern[0] === hirik,
            holam: patternLength(i) === 1 && tokens[i].pattern[0] === holam,
            'small-txt':
              (tokens[i].pattern.length === 1 &&
                tokens[i].pattern[0] === nikudVals[0]) ||
              tokens[i].pattern.length === nikudVals.length,
          }"
          :disabled="tokens[i].letters.includes('*')"
          :value="
            patternLength(i) === nikudVals.length
              ? 'הכל'
              : patternLength(i) > 1
              ? patternLength(i)
              : tokens[i].pattern[0] === nikudVals[0]
              ? 'ללא'
              : tokens[i].pattern[0]
          "
          @focus="
            nikudPatternClicked(i)
            currentIndex = i
            wordFocused = false
          "
          @input="nikudPatternInput"
          @keydown="nikudPatternInput"
          @click="
            nikudPatternClicked(i)
            currentIndex = i
            wordFocused = false
          "
          @keydown.right="rightArrowPress(i)"
          @keydown.left="leftArrowPress(i)"
          @keydown.up="upDownArrowPress(i)"
          @keydown.down="upDownArrowPress(i)"
          @keydown.backspace="deletePattern(i)"
          @keydown.delete="deletePattern(i)"
        />
      </span>
    </div>
    <div id="letter" class="btn-wrap bg-white px-3 pt-2 pb-2 mt-2 mb-2">
      <div
        class="d-flex justify-content-between"
        style="cursor: pointer"
        @click="showLetterButtons = !showLetterButtons"
      >
        <div>אות</div>
        <div>
          <i class="fas fa-caret-up" v-if="showLetterButtons"></i>
          <i class="fas fa-caret-down" v-else></i>
        </div>
      </div>
      <div v-if="showLetterButtons">
        <hr class="pt-0 mt-2 mb-0" />
        <div class="ml-n3 pt-3">
          <b-btn
            :disabled="
              item !== '*' && tokens[currentIndex].letters.indexOf('*') > -1
            "
            class="p-0 text-center f-narkis"
            :class="{
              'selected-syllable': tokens[currentIndex].letters.includes(item),
            }"
            variant="background"
            v-for="(item, i) in letterOptions"
            :key="i"
            @click="onLetterInput(item)"
          >
            {{ item }}
          </b-btn>
        </div>
        <div class="controls">
          <div class="border-bottom mb-1 pb-1">
            <small>{{
              hebrew ? "רצף אותיות כלשהו, יש לסמן ב" : "Any letter/nikud"
            }}</small>
            <div class="bg-lb rounded d-inline-block text-center mx-1">*</div>
            <br />
            <small>{{
              hebrew ? "אין חשיבות לאות, יש לסמן ב" : "Ignore letters"
            }}</small>
            <div class="bg-lb rounded d-inline-block text-center question mx-1">
              ?
            </div>
          </div>
          <div class="root-wrap" @click="rootAreaClicked">
            <b-form-group
              class="mb-0"
              :disabled="tokens[currentIndex].letters.indexOf('*') > -1"
            >
              <b-form-radio
                class="d-inline-block"
                v-model="tokens[currentIndex].rootLetter"
                name="root-letter-radios"
                size="sm"
                :value="dageshRootOptions.BOTH"
              >
                {{ hebrew ? "אות שורשית/שאינה" : "Root/Standard letter" }}
              </b-form-radio>
              <b-form-radio
                class="d-inline-block"
                v-model="tokens[currentIndex].rootLetter"
                name="root-letter-radios"
                size="sm"
                :value="dageshRootOptions.WITH"
              >
                {{ hebrew ? "שורשית" : "Root letter" }}
              </b-form-radio>
              <b-form-radio
                class="d-inline-block"
                v-model="tokens[currentIndex].rootLetter"
                name="root-letter-radios"
                size="sm"
                :value="dageshRootOptions.NONE"
              >
                {{ hebrew ? "לא-שורשית" : "Standard letter" }}
              </b-form-radio>
            </b-form-group>
          </div>
          <div class="optional-wrap border-top pt-1 mt-1">
            <b-form-group
              class="mb-0"
              :disabled="tokens[currentIndex].letters.indexOf('*') > -1"
            >
              <b-form-radio
                class="d-inline-block"
                v-model="tokens[currentIndex].optional"
                name="optional-letter-radios"
                size="sm"
                :value="optionalLetterOptions.REQUIRED"
              >
                {{ hebrew ? "אות הכרחית" : "Required letter" }}
              </b-form-radio>

              <b-form-radio
                class="d-inline-block"
                v-model="tokens[currentIndex].optional"
                name="optional-letter-radios"
                size="sm"
                :value="optionalLetterOptions.OPTIONAL"
              >
                {{ hebrew ? "אות אופציונלית" : "Optional letter" }}
              </b-form-radio>
            </b-form-group>
          </div>
        </div>
      </div>
    </div>
    <div id="nikud" class="btn-wrap bg-white px-3 pt-2 pb-2 mt-2 mb-2">
      <div
        class="d-flex justify-content-between"
        style="cursor: pointer"
        @click="showNikudButtons = !showNikudButtons"
      >
        <div>ניקוד</div>
        <div>
          <i class="fas fa-caret-up" v-if="showNikudButtons"></i>
          <i class="fas fa-caret-down" v-else></i>
        </div>
      </div>
      <div v-if="showNikudButtons">
        <hr class="pt-0 mt-2 mb-0" />
        <div class="nikud-btns ml-n3 pt-3">
          <b-btn
            :disabled="tokens[currentIndex].letters.indexOf('*') > -1"
            variant="background"
            class="p-0 text-center"
            :class="{ 'selected-syllable': tokens[currentIndex].anynikud }"
            @click="allClicked"
          >
            <span class="h-100 d-block overflow-hidden">{{
              hebrew ? "הכל" : "All"
            }}</span>
          </b-btn>
          <b-btn
            v-for="(item, i) in nikudVals"
            :key="i"
            :disabled="tokens[currentIndex].letters.indexOf('*') > -1"
            variant="background"
            class="p-0 text-center"
            :class="{
              'f-narkis': i > 0,
              'nikud-btn': i > 0 && i < nikudVals.length - 2,
              hirik: item === hirik,
              holam: item === holam || item === hirik,
              'selected-syllable':
                (tokens[currentIndex].pattern.length > 0 &&
                  tokens[currentIndex].pattern.indexOf(item) > -1) ||
                disabledNikud.indexOf(item) > -1 ||
                tokens[currentIndex].anynikud,
            }"
            @click="addSelectedNikud(item)"
            ><span
              class="d-block"
              :class="{
                'h-100': item !== hirik,
                'h-50': item === hirik,
                'mt-n2 overflow-hidden':
                  i > 0 &&
                  item !== holam &&
                  item !== hirik &&
                  i < nikudVals.length - 2,
                'mt-n1': i >= nikudVals.length - 2,
              }"
            >
              {{
                item === nikudVals[0] ? "ללא" : item === hirik ? holam : item
              }}
            </span>
          </b-btn>
          <br />
          <!-- <b-btn
            variant="background"
            class="f-narkis p-0 mr-0 text-center mb-0"
            :class="{'selected-syllable':tokens[currentIndex].anynikud || tokens[currentIndex].pattern.indexOf('שׁ')>-1}"
            @click="addSelectedNikud('שׁ')"       
          > <span class="mt-n1 d-block">שׁ</span>
          </b-btn>
          <b-btn
            variant="background"
            @click="addSelectedNikud('שׂ')"
            :class="{'selected-syllable':tokens[currentIndex].anynikud || tokens[currentIndex].pattern.indexOf('שׂ')>-1}"
            class="f-narkis p-0 text-center mb-0"       
          > <span class="mt-n1 d-block">שׂ</span>
          </b-btn> -->
        </div>
        <div class="syllables mt-2 border-bottom mb-2 pb-2">
          <small class="d-block mb-1">{{
            hebrew ? "צלילים" : "Syllables"
          }}</small>
          <b-btn
            v-for="item in Object.keys(syllables)"
            :key="item"
            variant="background"
            :disabled="tokens[currentIndex].letters.indexOf('*') > -1"
            class="p-0 text-center"
            :class="{
              'selected-syllable':
                tokens[currentIndex].pattern.includes(item) ||
                tokens[currentIndex].pattern.length === nikudVals.length ||
                syllables[item].every((v) =>
                  tokens[currentIndex].pattern.includes(v)
                ),
            }"
            @click="addSelectedNikud(item)"
          >
            {{ item }}
          </b-btn>
        </div>
        <div class="dagesh" @click="dageshAreaClicked">
          <b-form-group
            class="mb-0"
            :disabled="tokens[currentIndex].letters.includes('*')"
          >
            <b-form-radio
              class="d-inline-block"
              v-model="tokens[currentIndex].dagesh"
              name="dagesh-radios"
              size="sm"
              :value="dageshRootOptions.BOTH"
            >
              {{ hebrew ? "עם/ללא דגש" : "With/Without Dagesh" }}
            </b-form-radio>
            <b-form-radio
              class="d-inline-block"
              v-model="tokens[currentIndex].dagesh"
              name="dagesh-radios"
              size="sm"
              :value="dageshRootOptions.WITH"
            >
              {{ hebrew ? "עם דגש" : "With Dagesh" }}
            </b-form-radio>
            <b-form-radio
              class="d-inline-block"
              v-model="tokens[currentIndex].dagesh"
              name="dagesh-radios"
              size="sm"
              :value="dageshRootOptions.NONE"
            >
              {{ hebrew ? "ללא דגש" : "Without Dagesh" }}
            </b-form-radio>
          </b-form-group>
        </div>
      </div>
    </div>
    <div class="d-flex justify-content-end pt-1">
      <b-btn
        id="clear-btn"
        class="bg-transparent text-underline border-0 text-primary mb-1 clear-btn"
        :disabled="JSON.stringify(tokens) === JSON.stringify(blankTokens)"
        variant="link"
        @click="clearData"
      >
        {{ hebrew ? "ניקוי" : "Clear" }}
      </b-btn>
      <b-btn
        id="search-btn"
        variant="primary"
        class="mb-1"
        @click="searchClicked"
        :disabled="JSON.stringify(tokens) === JSON.stringify(blankTokens)"
      >
        {{ hebrew ? "חיפוש" : "Search" }}
      </b-btn>
    </div>
  </div>
</template>
<script>
function gtag() {
  window.dataLayer.push(arguments)
}
const hebrewKeyboard = {
  e: "\u05E7",
  r: "\u05E8",
  t: "\u05D0",
  y: "\u05D8",
  u: "\u05D5",
  i: "\u05DF",
  o: "\u05DD",
  p: "\u05E4",
  a: "\u05E9",
  s: "\u05D3",
  d: "\u05D2",
  f: "\u05DB",
  g: "\u05E2",
  h: "\u05D9",
  j: "\u05D7",
  k: "\u05DC",
  l: "\u05DA",
  ";": "\u05E3",
  z: "\u05D6",
  x: "\u05E1",
  c: "\u05D1",
  v: "\u05D4",
  b: "\u05E0",
  n: "\u05DE",
  m: "\u05E6",
  ",": "\u05EA",
  ".": "\u05E5",
}
import {
  getCurrentLetterDagesh,
  getLetterWithReversedDageshFromLetterArray,
  NIKUD,
  stringWithoutNikud,
} from "@/js/commonHebrew"
import { Actions } from "@/store/stateChanges"
import MainSearch from "@/components/MainSearch"
//import _ from "lodash"

export default {
  name: "SearchPattern",
  components: { MainSearch },
  data() {
    return {
      showImportWord: false,
      importNikudOnly: true,
      wordSelectedFromDD: false,
      searchWordInput: "",
      notInList: false,
      showLetterButtons: false,
      letterOptions: [],
      dageshRootOptions: {
        BOTH: "M",
        WITH: "Y",
        NONE: "N",
      },
      optionalLetterOptions: {
        OPTIONAL: true,
        REQUIRED: false,
      },
      hirik: NIKUD.HIRIQ,
      holam: NIKUD.HOLAM,
      showNikudButtons: false,
      nikudVals: [
        "X",
        NIKUD.QAMATZ,
        NIKUD.QAMATZ_QATAN,
        NIKUD.PATAH,
        NIKUD.TSERE,
        NIKUD.SEGOL,
        NIKUD.HIRIQ,
        NIKUD.SHEVA,
        NIKUD.HOLAM,
        NIKUD.QUBUTZ,
        NIKUD.HATAF_QAMATZ,
        NIKUD.HATAF_PATAH,
        NIKUD.HATAF_SEGOL,
        "שׁ",
        "שׂ",
      ],
      syllables: {
        A: [NIKUD.HATAF_PATAH, NIKUD.PATAH, NIKUD.QAMATZ],
        E: [NIKUD.HATAF_SEGOL, NIKUD.TSERE, NIKUD.SEGOL],
        I: [NIKUD.HIRIQ],
        O: [NIKUD.HOLAM, NIKUD.QAMATZ_QATAN, NIKUD.HATAF_QAMATZ],
        U: [NIKUD.QUBUTZ],
      },
      disabledNikud: [],
      currentIndex: 0,
      notAllowedDagesh: ["ח", "ע", "ם", "ן", "ץ"], //:disabled="notAllowedDagesh.indexOf(currentLetter)>-1"
      blankTokens: [],
      tokens: [],
      wordFocused: false,
      isSafari: false,
    }
  },
  created() {
    for (let i = "א".charCodeAt(0); i <= "ת".charCodeAt(0); i++) {
      this.letterOptions.push(String.fromCharCode(i))
    }
    this.letterOptions = this.letterOptions.concat(["*", "?"])
    this.tokens = []
    for (let i = 0; i < 8; i++) {
      this.tokens.push({
        letters: [],
        rootLetter: this.dageshRootOptions.BOTH,
        pattern: [],
        dagesh: this.dageshRootOptions.BOTH,
        optional: this.optionalLetterOptions.REQUIRED,
        anynikud: false,
      })
    }
  },
  mounted() {
    // if (!this.mobile) {
    this.blankTokens = JSON.parse(JSON.stringify(this.tokens))
    this.$nextTick(() => {
      if (this.savedTokens.length > 0 && this.searchedWord === "") {
        this.tokens = JSON.parse(JSON.stringify(this.savedTokens))
      } else if (this.$route.query.tokens0) {
        this.tokens = []
        const filteredData = Object.fromEntries(
          Object.entries(this.$route.query).filter(([key]) =>
            key.startsWith("tokens")
          )
        )
        let params = JSON.stringify(filteredData)
        let paramArr = Object.values(JSON.parse(params))
        for (let i = 0; i < 8; i++) {
          if (JSON.parse(paramArr[i])) this.tokens[i] = JSON.parse(paramArr[i])
        }
        this.$refs.wordPattern[0].focus()
        // this.$store.dispatch(Actions.RELOAD_FROM_URL);
      } else {
        this.setPattern(true)
        /* for (let i = 0; i < 9; i++) {
            this.tokens.push ({letters: [], rootLetter: this.dageshRootOptions.BOTH, pattern: [], dagesh: this.dageshRootOptions.BOTH, anynikud: false})
          } */
      }
      if (this.$refs.wordPattern[0]) this.$refs.wordPattern[0].focus()
    })
    var ua = navigator.userAgent.toLowerCase()
    if (ua.indexOf("firefox") > -1) {
      this.$refs.patternWrap.classList.add("firefox")
    } else if (ua.indexOf("safari") != -1) {
      if (ua.indexOf("chrome") > -1) {
        //alert("1") // Chrome
      } else {
        this.$refs.patternWrap.classList.add("safari") //safari
        this.isSafari = true
      }
    }
    // }
  },
  methods: {
    wordImported() {
      gtag("event", "Pattern word imported", {
        event_category: "selection",
        event_label: this.searchedWord,
      })
      this.setPattern()
    },
    setPattern(run) {
      this.tokens = JSON.parse(JSON.stringify(this.blankTokens))
      this.disabledNikud = []
      this.currentLetter = []
      this.currentIndex = 0
      let searchedWord = this.searchedWord
      let shinReplacement = this.importNikudOnly ? "" : "ש"
      if (searchedWord !== "") {
        //this.searchedWord
        let splitWord = searchedWord.split(/(?=[^ְֱֲֳִֵֶַָׇֹֻּׁׂ|])/)
        splitWord.forEach((item, index) => {
          if (item.includes("שׁ") || item.includes("שׂ")) {
            if (!this.importNikudOnly)
              this.tokens[index].pattern.push(item.includes("שׁ") ? "שׁ" : "שׂ")
            item = item.replace(/ש[ׁׂ]/g, shinReplacement)
          }
          if (this.importNikudOnly) {
            if (item.length > 1 && item.includes("ו")) {
              //  this.$refs.wordPattern[index].value = 'ו'
              this.tokens[index].letters.push("ו")
            }
            if (item.includes("י")) {
              //this.$refs.wordPattern[index].value = 'י'
              this.tokens[index].letters.push("י")
            }
          } else {
            //this.$refs.wordPattern[index].value = stringWithoutNikud(item)
            this.tokens[index].letters.push(stringWithoutNikud(item))
          }
          if (getCurrentLetterDagesh(item)) {
            this.tokens[index].dagesh = this.dageshRootOptions.WITH
            item = getLetterWithReversedDageshFromLetterArray(item)
          }
          if (item.replace(/[א-ת]/g, "").length === 0) {
            item = this.nikudVals[0]
          }

          let foundPatternIndex = this.nikudVals.indexOf(
            item.replace(/[א-ת]/g, "")
          )
          this.tokens[index].pattern.push(this.nikudVals[foundPatternIndex])

          /* this.tokens[index].letter = stringWithoutNikud(item)
          console.log(stringWithoutNikud(item))
          this.$refs.wordPattern[index].value = stringWithoutNikud(item) */
        })
        this.$refs.wordPattern[this.currentIndex].focus()
      }
      if (
        run &&
        JSON.stringify(this.tokens) !== JSON.stringify(this.blankTokens)
      ) {
        this.searchClicked()
      }
    },
    onImportShow() {
      this.wordSelectedFromDD = false
      setTimeout(() => {
        if (document.getElementById("search-input-dd"))
          document.getElementById("search-input-dd").focus()
      }, 50)
    },
    importFocusOut() {
      setTimeout(() => {
        this.$refs.wordPattern[0].focus()
      }, 50)
    },
    closeImportWord() {
      //this.setPattern()
      this.showImportWord = false
    },
    clearData() {
      this.tokens = JSON.parse(JSON.stringify(this.blankTokens))
      this.disabledNikud = []
      this.currentLetterד = []
      this.$refs.wordPattern[0].focus()
      this.$store.commit("CLEAR_RESULTS")
      this.$store.commit("SET_MODEL", "tavnit")
      /*   for (let i = 0; i < 8; i++) {
        this.$refs.wordPattern[i].value = ''
      } */
    },
    dageshAreaClicked() {
      this.$refs.nikudPattern[this.currentIndex].focus()
      // this.searchClicked()
    },
    rootAreaClicked() {
      this.$refs.wordPattern[this.currentIndex].focus()
      //this.searchClicked()
    },
    patternLength(i) {
      let disabledNikud = []
      Object.keys(this.syllables).map((item) => {
        if (this.tokens[i].pattern.indexOf(item) > -1) {
          disabledNikud = disabledNikud.concat(this.syllables[item])
        }
      })
      let selectedPattern = Array.from(
        new Set(this.tokens[i].pattern.concat(disabledNikud))
      )
      selectedPattern = selectedPattern.filter((e) => {
        return Object.keys(this.syllables).indexOf(e) == -1
      })
      if (
        this.tokens[i].pattern.length === 1 &&
        Object.keys(this.syllables).indexOf(this.tokens[i].pattern[0]) > -1
      ) {
        return 1
      }
      return selectedPattern.length
    },
    searchClicked() {
      gtag("event", "Pattern searched", {
        event_category: "selection",
      })
      let tokensReversed = [...this.tokens].reverse()
      let i = 0
      while (
        tokensReversed[i].pattern.length === 0 &&
        tokensReversed[i].letters.length === 0 &&
        tokensReversed[i].rootLetter === this.dageshRootOptions.BOTH &&
        tokensReversed[i].dagesh === this.dageshRootOptions.BOTH &&
        tokensReversed[i].optional === this.optionalLetterOptions.REQUIRED
      ) {
        i++
      }
      let indexToFill = this.tokens.length - i
      let searchItems = []
      this.tokens.map((obj, index) => {
        if (index < indexToFill) {
          if (obj.letters.length === 0) {
            obj.letters.push("?")
            //this.$refs.wordPattern[index].value = '?'
          }
          if (obj.pattern.length === 0 && !obj.letters.includes("*")) {
            this.tokens[index].pattern = this.nikudVals.slice()
            this.tokens[index].anynikud = true
          }

          let searchItem = {}
          if (obj.letters.includes("*")) {
            if (
              (this.tokens[index - 1] &&
                !this.tokens[index - 1].letters.includes("*")) ||
              index == 0
            )
              searchItem.wildcard = true
          } else {
            if (obj.letters[0] === "?") {
              searchItem.letters = []
              searchItem.anyletter = true
            } else {
              searchItem.letters = obj.letters
            }
            searchItem.dag = obj.dagesh
            searchItem.rootLetter = obj.rootLetter
            searchItem.optional = obj.optional
            searchItem.nikud = []
            if (obj.anynikud) {
              searchItem.anynikud = true
            } else {
              obj.pattern.map((nikudItem) => {
                if (nikudItem === this.nikudVals[0]) searchItem.nikud.push("!")
                else if (Object.keys(this.syllables).indexOf(nikudItem) > -1)
                  searchItem.nikud = searchItem.nikud.concat(
                    this.syllables[nikudItem]
                  )
                else if (nikudItem === "שׁ") {
                  if (obj.pattern.indexOf("שׂ") < 0) searchItem.shin = "\u05C1"
                } else if (nikudItem === "שׂ") {
                  if (obj.pattern.indexOf("שׁ") < 0) searchItem.shin = "\u05C2"
                } else searchItem.nikud.push(nikudItem)
              })
            }
          }
          searchItems.push(searchItem)
          searchItems = searchItems.filter(
            (value) => Object.keys(value).length !== 0
          )
        }
      })
      this.$store.commit("SET_TAVNIT_PARAMS", {
        tokens: this.tokens,
        itemsForApi: searchItems,
      })
      this.$store.dispatch(Actions.RUN_CHARUZIT)
    },
    allClicked() {
      this.tokens[this.currentIndex].anynikud =
        !this.tokens[this.currentIndex].anynikud
      if (this.tokens[this.currentIndex].anynikud) {
        this.tokens[this.currentIndex].pattern = this.nikudVals.slice()
        // this.tokens[this.currentIndex].pattern.shift()
      } else {
        this.tokens[this.currentIndex].pattern = []
      }
      this.updateDisabledNikud()
      this.$refs.nikudPattern[this.currentIndex].focus({ preventScroll: true })
    },
    nikudPatternInput(event) {
      //do not allow typing in input
      event.preventDefault()
      return false
      /* if (event.inputType !== 'deleteContentBackward'
        && event.inputType !== 'deleteContentForward'
        ) {
        //prevent input
        this.$refs.nikudPattern[this.currentIndex].value = this.$refs.nikudPattern[this.currentIndex].value.slice(0, -1)
      }  */
    },
    setCurrentLetter(index) {
      this.currentIndex = index
      this.updateDisabledNikud()
    },
    onLetterInput(event) {
      let index = this.currentIndex
      const typedLetter = event.key ? event.key : event
      const existingLetterIndex =
        this.tokens[index].letters.indexOf(typedLetter)
      if (!event.key) {
        //letter if from clicked letter button
        if (!this.tokens[index].letters.includes(event)) {
          this.tokens[index].letters.push(event)
        } else {
          this.tokens[index].letters.splice(existingLetterIndex, 1)
        }
      } else {
        //char was typed
        if (!event.key.includes("Arrow")) {
          let typedLetter = ""
          if ((event.shiftKey && event.key === "?") || event.key === "?") {
            typedLetter = "?"
          } else if (
            (event.shiftKey && event.key === "*") ||
            event.key === "*"
          ) {
            typedLetter = "*"
          } else if (!event.key.match(/[א-ת]/i)) {
            typedLetter = hebrewKeyboard[event.key.toLowerCase()]
            if (!typedLetter) typedLetter = ""
          } else if (event.key.match(/[א-ת]/i)) {
            typedLetter = event.key
          }
          if (event.key !== "Backspace" && event.key !== "Delete") {
            if (
              typedLetter !== "" &&
              !this.tokens[index].letters.includes(typedLetter)
            ) {
              this.tokens[index].letters.push(typedLetter)
              this.$refs.wordPattern[index].value =
                this.tokens[this.currentIndex].letters.length > 1
                  ? this.tokens[this.currentIndex].letters.length
                  : this.tokens[this.currentIndex].letters[0]
              if (this.tokens[this.currentIndex].letters.includes("*")) {
                this.tokens[index].pattern = []
                this.tokens[index].dagesh = this.dageshRootOptions.BOTH
                this.tokens[index].rootLetter = this.dageshRootOptions.BOTH
                this.tokens[index].optional =
                  this.optionalLetterOptions.REQUIRED
              }
            } else if (
              typedLetter !== "" &&
              this.tokens[index].letters.includes(typedLetter)
            ) {
              this.tokens[index].letters.splice(existingLetterIndex, 1)
            }
          } else {
            this.tokens[index].letters = []
            this.$refs.wordPattern[index].value = ""
          }
        }
      }
    },
    deletePattern(index) {
      this.tokens[index].pattern = []
      this.tokens[index].anynikud = false
      this.updateDisabledNikud()
    },
    leftArrowPress(index) {
      let refObj = this.$refs.nikudPattern
      if (this.wordFocused) {
        refObj = this.$refs.wordPattern
      }
      if (index + 1 < this.tokens.length) {
        refObj[index + 1].focus()
        this.currentIndex = index + 1
      } else {
        refObj[0].focus()
        this.currentIndex = 0
      }
    },
    rightArrowPress(index) {
      let refObj = this.$refs.nikudPattern
      if (this.wordFocused) {
        refObj = this.$refs.wordPattern
      }
      if (index > 0) {
        refObj[index - 1].focus()
        this.currentIndex = index - 1
      } else {
        refObj[this.tokens.length - 1].focus()
        this.currentIndex = this.tokens.length - 1
      }
    },
    upDownArrowPress(i) {
      if (this.wordFocused) {
        this.$refs.nikudPattern[i].focus()
      } else this.$refs.wordPattern[i].focus()
    },
    nikudPatternClicked(i) {
      if (!this.tokens[i].letters.includes("*")) {
        this.wordFocused = false
        this.currentIndex = i
        this.updateDisabledNikud()
      }
    },
    updateDisabledNikud() {
      this.disabledNikud = []
      Object.keys(this.syllables).map((item) => {
        if (this.tokens[this.currentIndex].pattern.indexOf(item) > -1) {
          this.disabledNikud = this.disabledNikud.concat(this.syllables[item])
        }
      })
    },
    addSelectedNikud(val) {
      //if this.currentLetters[0] &&
      if (!this.tokens[this.currentIndex].letters.includes("*")) {
        this.$refs.nikudPattern[this.currentIndex].focus({
          preventScroll: true,
        })
        let existingIndex = this.tokens[this.currentIndex].pattern.indexOf(val)
        if (existingIndex > -1) {
          //if exists in pattern, remove
          this.tokens[this.currentIndex].pattern.splice(existingIndex, 1)
          this.updateDisabledNikud()
        } else {
          var syl = ""
          let indexFound = -1
          //added item that is part of a syllable
          Object.values(this.syllables).map((arr, index) => {
            if (arr.indexOf(val) > -1) {
              indexFound = arr.indexOf(val)
              syl = Object.keys(this.syllables)[index]
            }
          })
          if (
            indexFound > -1 &&
            this.tokens[this.currentIndex].pattern.indexOf(syl) > -1
          ) {
            //if syllable already set, we need to remove above value, unset syllable but keep remaining items
            this.tokens[this.currentIndex].pattern.splice(
              this.tokens[this.currentIndex].pattern.indexOf(syl),
              1
            )
            let nikudValsToAdd = this.syllables[syl]
            nikudValsToAdd.map((val, index) => {
              if (
                index !== indexFound &&
                !this.tokens[this.currentIndex].pattern.indexOf(val) > -1
              ) {
                this.tokens[this.currentIndex].pattern.push(val)
              }
            })
          } else {
            //if syllable clicked but not in pattern, remove syllable values from pattern if all there
            if (
              Object.keys(this.syllables).indexOf(val) > -1 &&
              this.syllables[val].every(
                (v) => this.tokens[this.currentIndex].pattern.indexOf(v) > -1
              )
            ) {
              this.syllables[val].map((item) => {
                let indexToRemove =
                  this.tokens[this.currentIndex].pattern.indexOf(item)
                this.tokens[this.currentIndex].pattern.splice(indexToRemove, 1)
              })
            } else {
              //add item to pattern
              this.tokens[this.currentIndex].pattern.push(val)
            }
          }
          // }
        }
        this.tokens[this.currentIndex].anynikud =
          this.patternLength(this.currentIndex) === this.nikudVals.length
        Object.values(this.syllables).map((arr, index) => {
          if (
            JSON.stringify(arr.sort()) ===
            JSON.stringify(this.tokens[this.currentIndex].pattern.sort())
          ) {
            this.tokens[this.currentIndex].pattern = [
              Object.keys(this.syllables)[index],
            ]
          }
        })
        this.updateDisabledNikud()
      }
      //this.searchClicked()
    },
  },
  computed: {
    keymap() {
      return {
        enter: () => {
          if (this.showImportWord) {
            if (this.wordSelectedFromDD) {
              this.setPattern()
              this.closeImportWord()
              this.importFocusOut()
            }
          } else if (
            JSON.stringify(this.tokens) !== JSON.stringify(this.blankTokens)
          ) {
            this.$refs.wordPattern[this.currentIndex].focus()
            this.searchClicked()
          }
        },
      }
    },
    disableImportOk() {
      return this.notInList || this.searchWordInput === ""
    },
    mobile() {
      return this.$mq === "xs"
    },
    savedTokens() {
      return this.$store.state.patternTokens
    },
    searchedWord() {
      return this.$store.state.textForCharuzitApiCall
    },
    hebrew() {
      return this.$settings.hebrew
    },
  },
}
</script>
<style scoped lang="scss">
.pattern {
  background: #d6ecff;
  border-radius: 4px;
  @media (min-width: 992px) {
    margin-top: 37px;
  }
  .clear-btn {
    font-size: 13px;
  }
  .word-wrap,
  .nikud-wrap {
    label {
      font-size: 12px;
      width: 39px;
      margin-left: -4px;
      padding: 6px 0;
      text-align: center;
      height: 31px;
      vertical-align: bottom;
      color: #808080;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      background-color: #b9dfff;
    }
    .input-wrap {
      &.index-selected {
        background: white url("../assets/img/Input_a.svg");
      }
      &.word-index-selected {
        //box-shadow: 0px 0px 0px 1px rgba(0, 126, 229, 0.5) inset!important;
        background: url("../assets/img/Input_b.svg");
        //border: 1px solid var(--primary);
        &.bg-background {
          background: #f6f6f6;
          //border: 1px solid #f6f6f6;
          //  border: 1px solid var(--primary);
        }
      }
      margin-right: 4px;
      z-index: 1;
      overflow: hidden;
      border-radius: 4px;
      width: 32px;
      height: 32px;
      &:first-of-type {
        margin-right: 0;
      }
      &.not-dagesh-letter,
      &.dagesh-letter {
        &:before {
          position: absolute;
          content: "";
          font-size: 30px;
          right: 4px;
          bottom: 4px;
          height: 5px;
          width: 5px;
          border-radius: 50%;
          background: var(--primary);
          z-index: 2;
        }
      }
      &.not-dagesh-letter {
        &:before {
          background: rgba(0, 126, 229, 0.38);
        }
      }
      &.no-root,
      &.root {
        &:before {
          position: absolute;
          content: "";
          width: 15px;
          height: 15px;
          background: var(--primary);
          transform: rotate(45deg);
          bottom: -6px;
          right: -6px;
        }
      }
      &.no-root {
        &:before {
          background: rgba(0, 126, 229, 0.38);
        }
      }
    }
    input {
      border-radius: 4px;
      margin-right: 4px;
      width: 32px;
      height: 32px;
      &.multiple-items {
        &:before {
          position: absolute;
          content: "";
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--primary);
          bottom: 3px;
          right: 3px;
        }
      }
      &:focus,
      &:focus-within,
      &:active {
        outline: none;
      }
      &.index-selected:not(.nikud) {
        background: url("../assets/img/Input_a.svg");
      }
      &.word-index-selected:not(.nikud) {
        background: url("../assets/img/Input_b.svg");
      }
      &.optional-letter:not(.nikud) {
        background: url("../assets/img/Input_c.svg");
      }
      &:first-of-type {
        margin-right: 0;
      }
      &.small-txt {
        font-size: 12px;
      }
      &.f-narkis {
        font-size: 22px;
        line-height: 34px;

        &.mid-text {
          font-size: 19px;
        }
        &.smaller-text {
          font-size: 17px;
        }
        &.nikud {
          font-size: 40px;
          padding-bottom: 30px;
          width: 30px;
          margin: auto;
          top: -7px;
          line-height: 100;
          &.hirik {
            font-size: 90px;
            padding-bottom: 69px;
            top: -24px;
          }
          &.holam {
            padding-bottom: 0;
            top: 0;
            font-size: 90px;
            padding-top: 38px;
          }
        }
      }
    }
  }
  .nikud-wrap {
    input {
      caret-color: transparent;
    }
  }
  .btn-wrap {
    border-radius: 6px;
    box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.13);
    button.btn-background {
      &.f-narkis {
        font-size: 20px;
      }
      width: 34px;
      height: 34px;
      margin-left: 8px;
      margin-bottom: 9px;
      line-height: 34px;
      &.selected-syllable {
        border-color: var(--primary);
        position: relative;
        &:before {
          content: "\f058";
          position: absolute;
          background: white;
          border-radius: 50%;
          top: -6px;
          right: -6px;
          font-weight: 900;
          color: var(--primary);
          font-size: 14px;
          line-height: 14px;
          font-family: "Font Awesome 5 Free";
        }
      }
    }
  }
  .nikud-btns {
    button {
      //vertical-align: top;
      &.f-narkis {
        font-size: 22px;
        vertical-align: top;
        &.nikud-btn {
          font-size: 40px;
          line-height: 0;
          &.hirik {
            //font-size: 70px;
            line-height: 1;
            span {
              //margin-top: -25px;
              margin-top: -3px !important;
            }
          }
          &.holam {
            font-size: 70px;
            span {
              margin-top: 30px;
            }
          }
        }
      }
      font-size: 15px;
    }
  }
  .syllables {
    button {
      font-size: 18px;
      line-height: 18px;
    }
  }
  .controls {
    .bg-lb {
      height: 16px;
      width: 16px;
      vertical-align: middle;
      line-height: 20px;
      font-size: 17px;
      background: #d6ecff;
      &.question {
        font-size: 12px;
        line-height: 16px;
      }
    }
  }
}
.pattern.safari .nikud-wrap input.nikud {
  font-size: 28px;
  top: -10px;
  line-height: 1;
  &.holam {
    top: 0;
    font-size: 50px;
  }
}
.pattern.safari .word-wrap input.f-narkis {
  line-height: 1;
  position: absolute;
  top: 0;
}
.pattern.safari .nikud-btns button.f-narkis.nikud-btn span {
  margin-right: 5px;
}
.pattern.firefox .nikud-btns button.f-narkis.nikud-btn {
  font-family: roboto;
  font-size: 44px;
}
.pattern.firefox .nikud-btns button.f-narkis.nikud-btn.holam span {
  margin-top: 24px;
  font-size: 54px;
}
.pattern.firefox .nikud-btns button.f-narkis.nikud-btn.hirik span {
  margin-top: 1px;
  font-size: 54px;
  margin-left: 7px;
}
</style>
<style lang="scss">
.optional-wrap,
.root-wrap {
  margin-right: -10px;
}

.dagesh {
  margin-right: -7px;
}
.optional-wrap,
.dagesh,
.root-wrap {
  .custom-control-label:after,
  .custom-control-label:before {
    right: -17px;
  }
  .custom-radio.b-custom-control-sm,
  .input-group-sm .custom-radio {
    padding-left: 7px;
    padding-right: 25px;
  }
  .custom-radio .custom-control-input:checked ~ .custom-control-label:after {
    box-shadow: inset 0 0 0 5px var(--primary);
    border-radius: 50%;
    background: white;
  }
}
.root-wrap {
  .custom-radio.b-custom-control-sm,
  .input-group-sm .custom-radio {
    padding-left: 0px;
  }
}
</style>
<style lang="scss">
#import-word {
  .typeahead .spinner-wrap {
    left: 5px;
    .v-spinner {
      top: 20%;
    }
  }
  &.modal {
    position: relative;
    right: -15px;
  }
  .modal-sm {
    max-width: 350px;
    width: 350px;
    top: -15px;
    @media (max-width: 991px) {
      top: 0px;
      width: 100%;
    }
    .modal-body {
      height: 125px;
      //overflow: hidden;
    }
    .modal-title {
      font-size: 16px;
    }
    .close {
      display: none;
    }
  }
  .search-input-wrap {
    padding: 0 !important;
    margin-bottom: 10px !important;
  }
  .main-search {
    background-color: transparent;
    border-radius: 0;
    margin-top: 0;
    label {
      display: none !important;
    }
    input {
      border-radius: 4px;
      width: 100%;
      border: solid 1px #000000 !important;
    }
    .btn.ta-search-btn {
      display: none !important;
    }
  }
  .autocomplete-results {
    max-height: 120px;
  }
}
</style>
